import axios from 'axios';
import apiClient from '@/api/client';
import { getCookie } from '@/utils/cookies/functions';
import { handleError } from '@/frameworks/error';
import { ENDPOINTS, USERS } from '@/utils/endpoints/constants';

// ── Auth ──────────────────────────────────────────────────────────────────────

export async function login(username, password) {
  // Public endpoint — no auth header needed (apiClient adds it only when
  // the cookie exists, which it won't during the login call itself).
  const { data } = await apiClient.post('/authenticate', { username, password });
  return data;
}

export async function checkSession() {
  // Backend reads token from req.body.token (POST /check-session).
  // Previously this was incorrectly sent as GET; fixed here.
  const token = getCookie('Bearer');
  const { data } = await apiClient.post('/check-session', { token });
  return data;
}

export async function extendSession() {
  const token = localStorage.getItem('token');
  const { data } = await apiClient.post('/extend-session', { token });

  if (data && data.result) {
    const { token: newToken, validUntil } = data.result;
    localStorage.setItem('token', newToken);
    localStorage.setItem('timeout', validUntil);
  }

  return data;
}

// ── Users (admin) ─────────────────────────────────────────────────────────────

export async function getUsers() {
  const { data } = await apiClient.get('/users');
  return data.result;
}

export async function createUser(user) {
  const { data } = await apiClient.post('/users', user);
  return data;
}

export async function updateUser(userId, user) {
  const { data } = await apiClient.put(`/users/${userId}`, user);
  return data;
}

export async function deleteUser(userId) {
  const { data } = await apiClient.delete(`/users/${userId}`);
  return data;
}

// ── Role helper ───────────────────────────────────────────────────────────────

export async function checkUserRole(role) {
  const authData = await getAuthData();
  return authData.roles.includes(role);
}

export async function getAuthData() {
  const { data } = await apiClient.get(`/${USERS.getAuthData ?? 'users/me'}`);
  return data;
}

// ── Legacy functions (use ENDPOINTS.USERS — may not be active in this app) ───

export async function forgotPassword(email, emailSubject) {
  const response = await axios.request({
    method: 'post',
    url: ENDPOINTS.USERS + USERS.forgotPassword,
    headers: { accept: '*/*' },
    data: { email, emailSubject },
  }).catch(error => handleError(error));
  return response.data;
}

export async function resetPassword(email, token, password) {
  const response = await axios.request({
    method: 'post',
    url: ENDPOINTS.USERS + USERS.resetPassword,
    headers: { accept: '*/*' },
    data: { email, token, password },
  }).catch(error => handleError(error));
  return response.data;
}

export async function saveFilter(userId, name, filter) {
  const response = await axios.request({
    method: 'post',
    url: ENDPOINTS.USERS + USERS.saveFilter,
    headers: { accept: '*/*', Authorization: `Bearer ${getCookie('Bearer')}` },
    data: { userId, name, filter },
  }).catch(error => handleError(error));
  return response.data;
}

export async function editFilter(userId, previousName, updatedName, filter) {
  const response = await axios.request({
    method: 'post',
    url: ENDPOINTS.USERS + USERS.editFilter,
    headers: { accept: '*/*', Authorization: `Bearer ${getCookie('Bearer')}` },
    data: { userId, previousName, updatedName, filter },
  }).catch(error => handleError(error));
  return response.data;
}

export async function deleteFilter(userId, name) {
  const response = await axios.request({
    method: 'post',
    url: ENDPOINTS.AURORA + USERS.deleteFilter,
    headers: { accept: '*/*', Authorization: `Bearer ${getCookie('Bearer')}` },
    data: { userId, name },
  }).catch(error => handleError(error));
  return response.data;
}

export async function getFilters(userId) {
  const response = await axios.request({
    method: 'get',
    url: ENDPOINTS.USERS + USERS.getFilters + '/' + userId,
    headers: { accept: '*/*', Authorization: `Bearer ${getCookie('Bearer')}` },
  }).catch(error => handleError(error));
  return response.data;
}
